import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse } from "@/types";
import type {
  ICompanySetupProgress,
  IUpdateCompanySetupStepPayload,
  COMPANY_SETUP_STEP,
} from "@/types/(company)/company-setup.interface";
import {
  API_COMPANY_SETUP_DISMISS,
  API_COMPANY_SETUP_PROGRESS,
  API_COMPANY_SETUP_RESET,
  API_COMPANY_SETUP_RESTORE,
  API_COMPANY_SETUP_STEP,
} from "@/constants/apiEndpoints";

/**
 * Company setup ("go-live") guide.
 *
 * Every mutation returns the full recomputed progress, so each one writes it
 * straight into the `setupProgress` cache instead of invalidating and forcing a
 * refetch — the guide is one document, and a round trip would flash a skeleton
 * over the whole page for a single checkbox.
 */

/**
 * Guards the cache write below against out-of-order responses.
 *
 * Two mutations can be in flight at once (marking step A, then B, before A's
 * response lands) — each one's `onQueryStarted` writes the FULL document it
 * got back, so whichever response arrives last wins the cache regardless of
 * which request was sent last. If A's (now-stale) response arrives after B's,
 * it would silently roll the cache back to a snapshot computed before B's
 * change existed.
 *
 * Every write is stamped with a monotonically increasing sequence number
 * before the request goes out — a response applies its snapshot only if it
 * is still holding the highest sequence number seen. This module-level
 * counter (not per-request state) is what makes "still the latest" checkable
 * across independent `onQueryStarted` closures.
 */
let latestRequestSeq = 0;

/** Called synchronously when a mutation starts; the returned number is that request's ticket. */
const nextRequestSeq = () => ++latestRequestSeq;

/** True when `seq` is still the most recent request issued — i.e. nothing newer has started. */
const isStillLatest = (seq: number) => seq === latestRequestSeq;

export const companySetupApi = createApi({
  reducerPath: "companySetupApi",
  baseQuery: dynamicBaseQuery,
  refetchOnMountOrArgChange: true,
  tagTypes: ["setupProgress"],
  endpoints: (builder) => ({
    getCompanySetupProgress: builder.query<IApiResponse<ICompanySetupProgress>, void>({
      query: () => ({ url: API_COMPANY_SETUP_PROGRESS }),
      providesTags: ["setupProgress"],
    }),

    updateCompanySetupStep: builder.mutation<
      IApiResponse<ICompanySetupProgress>,
      { step: COMPANY_SETUP_STEP; body: IUpdateCompanySetupStepPayload }
    >({
      query: ({ step, body }) => ({
        url: API_COMPANY_SETUP_STEP(step),
        method: "PATCH",
        body,
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const seq = nextRequestSeq();
        const { data } = await queryFulfilled;
        if (!isStillLatest(seq)) return;
        dispatch(
          companySetupApi.util.upsertQueryData(
            "getCompanySetupProgress",
            undefined,
            data,
          ),
        );
      },
    }),

    resetCompanySetup: builder.mutation<IApiResponse<ICompanySetupProgress>, void>({
      query: () => ({ url: API_COMPANY_SETUP_RESET, method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const seq = nextRequestSeq();
        const { data } = await queryFulfilled;
        if (!isStillLatest(seq)) return;
        dispatch(
          companySetupApi.util.upsertQueryData(
            "getCompanySetupProgress",
            undefined,
            data,
          ),
        );
      },
    }),

    dismissCompanySetup: builder.mutation<IApiResponse<ICompanySetupProgress>, void>({
      query: () => ({ url: API_COMPANY_SETUP_DISMISS, method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const seq = nextRequestSeq();
        const { data } = await queryFulfilled;
        if (!isStillLatest(seq)) return;
        dispatch(
          companySetupApi.util.upsertQueryData(
            "getCompanySetupProgress",
            undefined,
            data,
          ),
        );
      },
    }),

    restoreCompanySetup: builder.mutation<IApiResponse<ICompanySetupProgress>, void>({
      query: () => ({ url: API_COMPANY_SETUP_RESTORE, method: "POST" }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const seq = nextRequestSeq();
        const { data } = await queryFulfilled;
        if (!isStillLatest(seq)) return;
        dispatch(
          companySetupApi.util.upsertQueryData(
            "getCompanySetupProgress",
            undefined,
            data,
          ),
        );
      },
    }),
  }),
});

export const {
  useGetCompanySetupProgressQuery,
  useUpdateCompanySetupStepMutation,
  useResetCompanySetupMutation,
  useDismissCompanySetupMutation,
  useRestoreCompanySetupMutation,
} = companySetupApi;
